<template>
  <div class="url-attributes-popup">
    <div class="url-attributes-popup__title">URL Attributes</div>
    <div v-if="!isErrorOnLoading && isDataLoaded" class="url-attributes-popup__table">
      <div class="url-attributes-popup__table_row">
        <div class="url-attributes-popup__table_header url-attributes-popup__label" >
          Attribute Name
        </div>
        <div class="url-attributes-popup__table_header url-attributes-popup__associated_urls" >
          Associated URLs
        </div>
      </div>
      <div
        v-for="data in tableData"
        :key="data._id"
        class="url-attributes-popup__table_row"
      >
        <div class="url-attributes-popup__table_cell url-attributes-popup__label" >
          <base-select
            borderLess
            alignLeft
            isRotation
            placeholder="Select attribute"
            :placeholderStyle="{ paddingLeft: '16px' }"
            :titleStyle="{ paddingLeft: '16px' }"
            :options="getListOfOptions(data._id)"
            v-model="data.value"
            @change="onURLAttributeChange(data)"
          />
        </div>
        <div
          class="url-attributes-popup__table_cell url-attributes-popup__associated_urls"
          :class="{ 'url-attributes-popup__associated_urls-clickable': !!data.associated_urls }"
          @click="showUrlList(data.value, !!data.associated_urls)"
        >
          {{ data.associated_urls }}
          <base-material-design-icon
            class="url-attributes-popup__cross"
            icon="plus"
            @click.stop="removeUrlAttributeRow(data._id)"
          />
        </div>
      </div>
      <div
        v-if="isAddNewPossible"
        class="url-attributes-popup__table_row url-attributes-popup__table_row_add-new"
        @click="addNew"
      >
        <base-material-design-icon
          class="url-attributes-popup__plus"
          icon="plus"
        />
        Add new row
      </div>
    </div>
    <div v-else-if="isDataLoaded" class="url-attributes-popup__no-data">
      Data Fetching Error
    </div>
    <div v-else class="url-attributes-popup__loader">
      <base-loader />
    </div>
    <button class="url-attributes-popup__close-btn" @click.prevent="close">close</button>
    <modal name="confirm-modal" v-bind="modalProps">
      <base-confirm-modal title="Are you sure you want to remove this URL's attribute?"
                          @confirm="confirmModal" />
    </modal>
    <modal name="attribute-association" v-bind="urlListModalProps">
      <UrlAttributesAssociationModal
        :urlAttributeId="urlAttributeId"
        @cancel="hideUrlList"
      />
    </modal>
  </div>
</template>

<script>
import BaseModalMixins from '@/common/BaseModalMixins'
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import UrlAttributesAssociationModal from './UrlAttributesAssociationModal'

export default {
  name: 'UrlAttributes',
  mixins: [BaseModalMixins],
  components: { UrlAttributesAssociationModal },
  props: {
    briefId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isDataLoaded: false,
      isSomethingChanged: false,
      isErrorOnLoading: false,
      removeId: '',
      urlAttributeId: '',
      addNewLength: 0
    }
  },
  watch: {
    briefId: {
      async handler (v) {
        if (v) {
          try {
            await this.getBriefURLAttributes(v)
          } catch (e) {
            this.isErrorOnLoading = true
          } finally {
            this.isDataLoaded = true
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('urlAttributes', {
      options (state) {
        return (state?.briefUrlAttributes?.options || []).map(i => ({ value: i._id, ...i }))
      },
      listOfAppliedUrls (state) {
        return state?.briefUrlAttributes?.content_brief_url_attributes || []
      }
    }),
    filteredOptions () {
      return _.cloneDeep(this.options.filter(i => !this.listOfAppliedUrls.includes(i._id)))
    },
    tableData () {
      const tableData = this.listOfAppliedUrls.map(i => _.cloneDeep(this.options.find(obj => obj._id === i)))
      for (let i = 1; i <= this.addNewLength; i++) {
        tableData.push({
          _id: '-' + 1,
          value: '',
          label: '',
          associated_urls: ''
        })
      }
      return tableData
    },
    isAddNewPossible () {
      return this.options.length > this.tableData.length
    },
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '566px' }
    },
    urlListModalProps () {
      return { ...baseModalProps, height: '90%', width: '690px' }
    }
  },
  methods: {
    ...mapActions('urlAttributes', ['getBriefURLAttributes', 'removeBriefURLAttribute', 'createBriefURLAttribute', 'changeBriefURLAttribute']),
    close () {
      this.$emit('cancel', this.isSomethingChanged)
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.removeUrlAttribute()
      }
    },
    removeUrlAttributeRow (id) {
      if (id.startsWith('-')) {
        this.addNewLength -= 1
      } else {
        this.removeId = id
        this.$modal.show('confirm-modal')
      }
    },
    addNew () {
      this.addNewLength += 1
    },
    getListOfOptions (id) {
      const options = _.cloneDeep(this.filteredOptions)
      const selectedOption = this.options.find(i => i._id === id)
      if (selectedOption) {
        options.push(_.cloneDeep(selectedOption))
      }
      return options
    },
    showUrlList (urlAttributeId, isClickable) {
      if (isClickable) {
        this.urlAttributeId = urlAttributeId
        this.$modal.show('attribute-association')
      }
    },
    hideUrlList () {
      this.$modal.hide('attribute-association')
    },
    async onURLAttributeChange (data) {
      if (data._id.startsWith('-')) {
        this.addNewLength -= 1
        const isSuccess = await this.createBriefURLAttribute({
          briefId: this.briefId,
          urlAttrId: data.value
        })
        if (!isSuccess && this.isAddNewPossible) {
          this.addNewLength += 1
        } else {
          this.isSomethingChanged = true
        }
      } else {
        this.changeBriefURLAttribute({ briefId: this.briefId, data })
      }
    },
    removeUrlAttribute () {
      this.isSomethingChanged = true
      this.removeBriefURLAttribute({ briefId: this.briefId, urlAttrId: this.removeId })
    }
  }
}
</script>

<style lang="scss">
  .url-attributes-popup {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 30px;

    .v--modal-overlay {
      background-color: transparent !important;
    }

    &__title {
      text-align: center;
      font-size: 32px;
      line-height: 1.5;
      letter-spacing: -0.32px;
      font-weight: bold;
      margin-bottom: 40px;
      color: $rfx-dark-march;
    }

    &__no-data {
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
    }

    &__no-data,
    &__loader {
      width: -webkit-fill-available;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__cross {
      position: absolute;
      font-size: 24px;
      transform: rotate(45deg);
      right: 10px;
      top: calc(50% - 12px);
      cursor: pointer;
    }

    &__plus {
      font-size: 20px;
      margin-right: 17px;
    }

    &__table {
      &_row {
        display: flex;
        border: 1px solid #e6e8ea;
        border-top: none;

        &:first-child {
          border-color: transparent;
        }

        &:nth-child(2) {
          border-top: 1px solid #e6e8ea;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }

        &:last-child {
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        &_add-new {
          padding: 9px 0 9px 16px;
          align-items: center;
          justify-content: center;
          text-transform: uppercase;
          font-size: 10px;
          line-height: 20px;
          letter-spacing: 1.25px;
          color: $rfx-dark-march;
          border-top: none;
          font-weight: 900;
          cursor: pointer;
        }
      }

      &_cell {
        position: relative;
        cursor: pointer;

        &.url-attributes-popup__label {
          padding: 0;
        }
      }

      &_header {
        font-weight: bold;
        height: 30px;
      }
    }

    &__label {
      flex: 3;
    }

    &__associated_urls {
      cursor: default;
      flex: 1;

      &-clickable {
        cursor: pointer;
      }
    }

    &__associated_urls.url-attributes-popup__table_cell {
      border-left: 1px solid #e6e8ea;
    }

    &__label,
    &__associated_urls {
      height: 20px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.16px;
      padding: 9px 0 9px 16px;
    }

    &__close-btn {
      color: #fff;
      letter-spacing: 1.25px;
      font-weight: 900;
      font-size: 10px;
      line-height: 4;
      padding: 0 32px;
      margin-top: 24px;
      background-color: #2f404e;
      align-self: flex-end;
      border-radius: 4px;
      border: none;
      text-transform: uppercase;
    }
  }
</style>
